import {
	BlockContextProvider,
	useBlockProps,
	useInnerBlocksProps,
	__experimentalUseBlockPreview as useBlockPreview,
	store as blockEditorStore,
} from '@wordpress/block-editor';
import { createBlock } from '@wordpress/blocks';
import { Spinner } from '@wordpress/components';
import { store as coreStore } from '@wordpress/core-data';
import { useSelect, useDispatch } from '@wordpress/data';
import { memo, useEffect, useMemo, useRef, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

import AllInspectorSettings from './InspectorControls';
import metadata from '../block.json';

import '../style.scss';

const PLACEHOLDER_IMAGES = [
	'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop',
	'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=800&h=600&fit=crop',
	'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&h=600&fit=crop',
	'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=800&h=600&fit=crop',
	'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?w=800&h=600&fit=crop',
	'https://images.unsplash.com/photo-1433086966358-54859d0ed716?w=800&h=600&fit=crop',
];

const STATIC_TEMPLATE = PLACEHOLDER_IMAGES.map( ( url ) => [
	'brandy/carousel-slide',
	{},
	[ [ 'core/image', { url } ] ],
] );

const DYNAMIC_TEMPLATE = [
	[
		'brandy/carousel-slide',
		{},
		[
			[ 'core/post-featured-image', { isLink: true } ],
			[ 'core/post-title', { level: 4, isLink: true } ],
		],
	],
];

export default function Edit( props ) {
	const { attributes, clientId } = props;
	const isDynamic = attributes.isDynamic ?? false;

	const { replaceInnerBlocks } = useDispatch( blockEditorStore );
	const prevIsDynamic = useRef( isDynamic );

	useEffect( () => {
		if ( prevIsDynamic.current === isDynamic ) {
			return;
		}
		prevIsDynamic.current = isDynamic;

		if ( isDynamic ) {
			// Switching to dynamic: replace with single slide using dynamic template.
			const slideBlock = createBlock(
				'brandy/carousel-slide',
				{},
				DYNAMIC_TEMPLATE[ 0 ][ 2 ].map( ( [ name, attrs ] ) =>
					createBlock( name, attrs )
				)
			);
			replaceInnerBlocks( clientId, [ slideBlock ] );
		}
	}, [ isDynamic, clientId, replaceInnerBlocks ] );

	return isDynamic ? (
		<DynamicEdit { ...props } />
	) : (
		<StaticEdit { ...props } />
	);
}

function StaticEdit( props ) {
	const { attributes } = props;

	const blockProps = useBlockProps( {
		className: 'brandy-carousel-wrapper',
		style: getEditorStyles( attributes ),
	} );

	const innerBlocksProps = useInnerBlocksProps(
		{ className: 'brandy-carousel__editor-grid' },
		{ template: STATIC_TEMPLATE, renderAppender: false }
	);

	return (
		<>
			<AllInspectorSettings { ...props } />
			<div { ...blockProps }>
				<div { ...innerBlocksProps } />
			</div>
		</>
	);
}

function SlideTemplateInnerBlocks() {
	const innerBlocksProps = useInnerBlocksProps(
		{ className: 'embla__slide brandy-carousel__slide' },
		{
			template: DYNAMIC_TEMPLATE[ 0 ][ 2 ],
			__unstableDisableLayoutClassNames: true,
		}
	);
	return <div { ...innerBlocksProps } />;
}

function SlidePreview( { blocks, blockContextId, isHidden, setActiveId } ) {
	const blockPreviewProps = useBlockPreview( {
		blocks,
		props: { className: 'embla__slide brandy-carousel__slide' },
	} );

	return (
		<div
			{ ...blockPreviewProps }
			role="button"
			tabIndex={ 0 }
			onClick={ () => setActiveId( blockContextId ) }
			onKeyDown={ ( e ) => {
			if ( e.key === 'Enter' || e.key === ' ' ) {
				setActiveId( blockContextId );
			}
		} }
			style={ { display: isHidden ? 'none' : undefined } }
		/>
	);
}

const MemoizedSlidePreview = memo( SlidePreview );

function DynamicEdit( props ) {
	const { attributes, clientId } = props;
	const query = attributes.query ?? metadata.attributes.query.default;

	const blockProps = useBlockProps( {
		className: 'brandy-carousel-wrapper',
		style: getEditorStyles( attributes ),
	} );

	const [ activePostId, setActivePostId ] = useState();

	// Stabilize query object for useSelect dependency.
	const queryKey = JSON.stringify( query );
	const stableQuery = useRef( query );
	useEffect( () => {
		stableQuery.current = query;
	}, [ queryKey ] );

	const { posts, blocks } = useSelect(
		( select ) => {
			const { getEntityRecords } = select( coreStore );
			const { getBlocks } = select( blockEditorStore );

			const q = stableQuery.current;
			const postType = q.postType || 'post';
			const orderBy = q.orderBy || 'date';
			const queryArgs = {
				per_page: q.perPage || 6,
				order: q.order || 'desc',
				offset: q.offset || 0,
				status: 'publish',
			};

			// REST API doesn't support 'rand' orderby — omit it to use default.
			if ( orderBy !== 'rand' ) {
				queryArgs.orderby = orderBy;
			}

			if ( q.categories?.length ) {
				queryArgs.categories = q.categories;
			}
			if ( q.tags?.length ) {
				queryArgs.tags = q.tags;
			}
			if ( q.exclude?.length ) {
				queryArgs.exclude = q.exclude;
			}

			return {
				posts: getEntityRecords( 'postType', postType, queryArgs ),
				blocks: getBlocks( clientId ),
			};
		},
		[ queryKey, clientId ]
	);

	const postContexts = useMemo(
		() =>
			posts?.map( ( post ) => ( {
				postType: post.type,
				postId: post.id,
			} ) ),
		[ posts ]
	);

	if ( ! posts ) {
		return (
			<>
				<AllInspectorSettings { ...props } />
				<div { ...blockProps }>
					<Spinner />
				</div>
			</>
		);
	}

	if ( ! posts.length ) {
		return (
			<>
				<AllInspectorSettings { ...props } />
				<div { ...blockProps }>
					<p>{ __( 'No results found.', 'brandy-blocks' ) }</p>
				</div>
			</>
		);
	}

	return (
		<>
			<AllInspectorSettings { ...props } />
			<div { ...blockProps }>
				<div className="brandy-carousel__editor-grid">
					{ postContexts.map( ( ctx ) => (
						<BlockContextProvider
							key={ ctx.postId }
							value={ ctx }
						>
							{ ctx.postId ===
							( activePostId ||
								postContexts[ 0 ]?.postId ) ? (
								<SlideTemplateInnerBlocks />
							) : null }
							<MemoizedSlidePreview
								blocks={ blocks }
								blockContextId={ ctx.postId }
								setActiveId={ setActivePostId }
								isHidden={
									ctx.postId ===
									( activePostId ||
										postContexts[ 0 ]?.postId )
								}
							/>
						</BlockContextProvider>
					) ) }
				</div>
			</div>
		</>
	);
}

function getEditorStyles( attributes ) {
	return {
		'--embla-slides-desktop':
			attributes.slides?.desktop?.toString() ??
			metadata.attributes.slides.default.desktop.toString(),
		'--embla-slides-tablet':
			attributes.slides?.tablet?.toString() ??
			metadata.attributes.slides.default.tablet.toString(),
		'--embla-slides-mobile':
			attributes.slides?.mobile?.toString() ??
			metadata.attributes.slides.default.mobile.toString(),
		'--embla-gap': attributes.itemsSpacing ?? '24px',
		...( ( attributes.navigation?.enabled ?? true )
			? {
					'--embla-navigation-display': 'flex',
					'--embla-button-width':
						attributes.navigation?.size ??
						metadata.attributes.navigation.default.size,
					'--embla-button-height':
						attributes.navigation?.size ??
						metadata.attributes.navigation.default.size,
					'--embla-button-icon-width':
						attributes.navigation?.iconSize ??
						metadata.attributes.navigation.default.iconSize,
					'--embla-button-icon-height':
						attributes.navigation?.iconSize ??
						metadata.attributes.navigation.default.iconSize,
					'--embla-button-background-color':
						attributes.navigation?.backgroundColor ??
						metadata.attributes.navigation.default.backgroundColor,
					'--embla-button-icon-color':
						attributes.navigation?.iconColor ??
						metadata.attributes.navigation.default.iconColor,
			  }
			: { '--embla-navigation-display': 'none' } ),
		...( attributes.pagination?.enabled ?? true
			? {
					'--embla-pagination-display': 'flex',
					'--embla-pagination-bullet-color':
						attributes.pagination?.defaultColor ??
						metadata.attributes.pagination.default.defaultColor,
					'--embla-pagination-bullet-color-active':
						attributes.pagination?.activeColor ??
						metadata.attributes.pagination.default.activeColor,
					'--embla-pagination-bullet-size':
						attributes.pagination?.size ??
						metadata.attributes.pagination.default.size,
					'--embla-pagination-bullet-spacing':
						attributes.pagination?.spacing ??
						metadata.attributes.pagination.default.spacing,
			  }
			: { '--embla-pagination-display': 'none' } ),
	};
}
